import { useState, useRef, useEffect } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";

const NAV_SECTIONS = [
  {
    label: "Platform Status",
    items: [
      { to: "/admin/status",    label: "Status Management", icon: "◉" },
      { to: "/admin/incidents", label: "Incidents",          icon: "⚠" },
      { to: "/admin/maintenance", label: "Maintenance",      icon: "🔧" },
      { to: "/admin/registry",  label: "Service Registry",   icon: "▤" },
      { to: "/admin/monitor",   label: "Monitor Dashboard",  icon: "◈" },
      { to: "/admin/third-party", label: "Third-Party Services", icon: "🌐" },
    ],
  },
  {
    label: "Observability",
    items: [
      { to: "/admin/logs",     label: "Logs",    icon: "≡" },
      { to: "/admin/metrics",  label: "Metrics", icon: "▲" },
      { to: "/admin/traces",   label: "Traces",  icon: "⤳" },
      { to: "/admin/errors",   label: "Errors",  icon: "✖" },
      { to: "/admin/slo",      label: "SLO",     icon: "◎" },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/admin/oncall",     label: "On-Call",    icon: "☎" },
      { to: "/admin/dashboards", label: "Dashboards", icon: "⊞" },
      { to: "/admin/workflows",  label: "Workflows",  icon: "⇄" },
      { to: "/admin/webhooks",   label: "Webhooks",   icon: "⇗" },
      { to: "/admin/templates",  label: "Templates",  icon: "📋" },
      { to: "/admin/subscribers", label: "Subscribers", icon: "👥" },
    ],
  },
];

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function parseNames(name: string): { firstName: string; lastName: string } {
  const parts = name.trim().split(/\s+/);
  return {
    firstName: parts[0] ?? "",
    lastName: parts.slice(1).join(" "),
  };
}

export function AdminLayout() {
  const { user, logout } = useAuth();
  const isSuperUser = user?.is_superuser === true;
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [showProfile, setShowProfile] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);

  function toggleSection(label: string) {
    setCollapsed((prev) => ({ ...prev, [label]: !prev[label] }));
  }

  async function handleLogout() {
    setShowProfile(false);
    await logout();
    navigate("/admin/login");
  }

  // Close profile dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (profileRef.current && !profileRef.current.contains(e.target as Node)) {
        setShowProfile(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <div className="ac-layout">

      {/* ── Sidebar ── */}
      <aside className="ac-sidebar" aria-label="Main navigation">
        {/* Brand */}
        <div className="ac-sidebar__brand">
          <img
            src="https://stshrnextgenstorage.blob.core.windows.net/aptean-theme/Aptean-logo-login.png"
            alt="Aptean"
            className="ac-sidebar__brand-logo"
          />
        </div>

        {/* Nav sections */}
        <nav className="ac-sidebar__nav">
          {NAV_SECTIONS.map((section) => {
            const isCollapsed = collapsed[section.label];
            return (
              <div key={section.label} className="ac-nav-section">
                <button
                  type="button"
                  className="ac-nav-section__toggle"
                  onClick={() => toggleSection(section.label)}
                  aria-expanded={!isCollapsed}
                >
                  <span className="ac-nav-section__label">{section.label}</span>
                  <span className="ac-nav-section__chevron" aria-hidden="true">
                    {isCollapsed ? "›" : "⌄"}
                  </span>
                </button>
                {!isCollapsed && (
                  <ul className="ac-nav-section__list">
                    {section.items.map(({ to, label, icon }) => (
                      <li key={to}>
                        <NavLink
                          to={to}
                          className={({ isActive }) =>
                            `ac-nav-link${isActive ? " ac-nav-link--active" : ""}`
                          }
                        >
                          <span className="ac-nav-link__icon" aria-hidden="true">{icon}</span>
                          <span className="ac-nav-link__label">{label}</span>
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            );
          })}
        </nav>

          {/* Administration section (superuser only) */}
          {isSuperUser && (
            <div className="ac-nav-section">
              <button
                type="button"
                className="ac-nav-section__toggle"
                onClick={() => toggleSection("Administration")}
                aria-expanded={!collapsed["Administration"]}
              >
                <span className="ac-nav-section__label">Administration</span>
                <span className="ac-nav-section__chevron" aria-hidden="true">
                  {collapsed["Administration"] ? "›" : "\u2304"}
                </span>
              </button>
              {!collapsed["Administration"] && (
                <ul className="ac-nav-section__list">
                  <li>
                    <NavLink
                      to="/admin/users"
                      className={({ isActive }) =>
                        `ac-nav-link${isActive ? " ac-nav-link--active" : ""}`
                      }
                    >
                      <span className="ac-nav-link__icon" aria-hidden="true">{"🔐"}</span>
                      <span className="ac-nav-link__label">User Access</span>
                    </NavLink>
                  </li>
                  <li>
                    <NavLink
                      to="/admin/subscription-settings"
                      className={({ isActive }) =>
                        `ac-nav-link${isActive ? " ac-nav-link--active" : ""}`
                      }
                    >
                      <span className="ac-nav-link__icon" aria-hidden="true">{"✉"}</span>
                      <span className="ac-nav-link__label">Subscription Settings</span>
                    </NavLink>
                  </li>
                </ul>
              )}
            </div>
          )}

        {/* Footer */}
        <div className="ac-sidebar__footer">
          <Link to="/" className="ac-sidebar__status-link">
            ← View Status Page
          </Link>
          <div className="ac-sidebar__logo-block">
            <img
              src="https://stshrnextgenstorage.blob.core.windows.net/aptean-theme/aptean.svg"
              alt="Aptean — Ready for What's Next, Now"
              className="ac-sidebar__aptean-logo"
            />
          </div>
        </div>
      </aside>

      {/* ── Body ── */}
      <div className="ac-body">

        {/* Top bar */}
        <header className="ac-topbar">
          <div className="ac-topbar__left">
            <h1 className="ac-topbar__title">StatusCentral</h1>
          </div>
          <div className="ac-topbar__right">
            {user && (
              <>
                <span className="ac-topbar__username">{user.name || user.email}</span>
                <div ref={profileRef} className="ac-profile-menu">
                  <button
                    type="button"
                    className="ac-avatar"
                    onClick={() => setShowProfile((v) => !v)}
                    aria-label={`Profile: ${user.name || user.email}`}
                    aria-expanded={showProfile}
                    aria-haspopup="true"
                  >
                    {getInitials(user.name || user.email || "U")}
                  </button>
                  {showProfile && (
                    <div className="ac-profile-dropdown" role="menu">
                      <div className="ac-profile-dropdown__header">
                        <div className="ac-profile-dropdown__avatar">
                          {getInitials(user.name || user.email || "U")}
                        </div>
                        <div className="ac-profile-dropdown__name">
                          {user.name || user.email}
                        </div>
                      </div>
                      <div className="ac-profile-dropdown__body">
                        {(() => {
                          const { firstName, lastName } = parseNames(user.name || "");
                          return (
                            <>
                              <div className="ac-profile-dropdown__field">
                                <span className="ac-profile-dropdown__label">First Name</span>
                                <span className="ac-profile-dropdown__value">{firstName || "—"}</span>
                              </div>
                              <div className="ac-profile-dropdown__field">
                                <span className="ac-profile-dropdown__label">Last Name</span>
                                <span className="ac-profile-dropdown__value">{lastName || "—"}</span>
                              </div>
                              <div className="ac-profile-dropdown__field">
                                <span className="ac-profile-dropdown__label">Email</span>
                                <span className="ac-profile-dropdown__value ac-profile-dropdown__value--email">{user.email}</span>
                              </div>
                            </>
                          );
                        })()}
                      </div>
                      <div className="ac-profile-dropdown__divider" />
                      <button
                        type="button"
                        className="ac-profile-dropdown__signout"
                        role="menuitem"
                        onClick={handleLogout}
                      >
                        Sign out
                      </button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="ac-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
